import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { Box, Link, Stack, Typography, Skeleton } from '@mui/material';
import {
	convertToMetricNoIntlFormat,
	DataGrid,
	getTenantId,
	GridColDef,
	GridFilterModel,
	GridLogicOperator,
	StylovyzeCard,
	Text,
	useIsFeatureEnabled,
	useUnits,
} from '@innovyze/stylovyze';
import { getGridSingleSelectOperators } from '@mui/x-data-grid';
import { getNewAssetIds } from '@innovyze/lib_asset_schema';

import { CustomDataGridHeader } from '@Components/Utils/CustomDataGridHeader';

import {
	getAssetBreakDownChartAction,
	getFailureGradeMatrixAction,
	listDeteriorationModelAssetsResultsAction,
	setAssetBackLabel,
	setPaginationState,
	setSelectedBreakdownGrade,
} from '@Actions';
import { useGlobalization } from '@Translations';
import { GetDeteriorationModelAssetsResultsPaginatedRequests } from '@Types/deteriorationModels.types';
import {
	selectAssetBreakDownChartStore,
	selectDeteriorationModel,
	selectDeteriorationModelAssetsResults,
	selectFailureDefinition,
	selectFailureGradeAggregatedCounts,
	selectFailureGradeMatrixResults,
	selectModelPaginationState,
	selectSelectedBreakdownGrade,
} from '@Selectors';
import { assetsResultParamsMapping } from '@Services/deteriorationModels.services';
import { addRouteNamespace } from '@Utils/actions';
import { getDefaultAssetsResultsRequest } from '@Utils/assetsResultsRequest';
import { encodeRouteParam } from '@Utils/urlFormatting';
import { AssetBreakDownChart } from '@Components/deteriorationModel/resultsTab/AssetBreakDownChart';
import { AssetCurveDialog, AssetCurveTarget } from '@Components/deteriorationModel/resultsTab/AssetCurveDialog';
import { GradeChip } from '@Components/deteriorationModel/utils/GradeChip';
import { diameterFilterToMetric, formatTotalLength } from '@Components/deteriorationModel/utils/unitDisplay';
import { hasStoredAssetsResults } from '@Components/deteriorationModel/utils/assetResultsCache';
import { renderHeaderWithTooltip } from '../utils/renderHeaderWithTooltip';

const GRADE_VALUE_OPTIONS = [
	{ value: 'negligible', label: 'Optimal' },
	{ value: 'low', label: 'Stable' },
	{ value: 'medium', label: 'Moderate' },
	{ value: 'high', label: 'Concerning' },
	{ value: 'extreme', label: 'Critical' },
];

interface AssetsResultsProps {
	initialGroupFilter?: string | null;
}

/**
 * The asset-schema id for the asset-details route. A model copies its types from its
 * asset group set, and group sets store the legacy spelling, so a Sanitary Sewer
 * model's asset type is "pipe"; the route needs "wwPipe". getNewAssetIds resolves the
 * legacy pair and passes schema ids through unchanged.
 */
const toAssetTypeId = (systemType: string | undefined, assetType: string | undefined): string | undefined => {
	if (!assetType) return undefined;
	try {
		return getNewAssetIds({ systemType: systemType ?? '', assetType }).assetTypeId.id || assetType;
	} catch (e) {
		// It passes unmapped types through today, but it runs in the render path of the
		// whole results grid: if a future lib version throws, fall back to the stored
		// type (the link may be wrong for a legacy spelling) rather than break the grid.
		console.error('Unable to resolve asset type for the asset details link', { systemType, assetType }, e);
		return assetType;
	}
};

export const AssetsResults = ({ initialGroupFilter }: AssetsResultsProps) => {
	const { t } = useGlobalization();
	const fpLabel = t('Failure Prob. (%)');
	const fpHeading = t('Failure Probability (%)');
	// prettier-ignore
	const fpContent = t('The likelihood that this asset has experienced or will experience failure at its current age, based on the survival curve of its group. Higher values indicate greater risk.');
	const rulLabel = t('RUL Years (80%)');
	const rulHeading = t('Remaining Useful Life - Years (80%)');
	// prettier-ignore
	const rulContent = t('Estimated years until this asset reaches 80% failure probability. Negative values mean the asset has already passed this threshold.');
	const histLabel = t('Past Failure Type');
	// prettier-ignore
	const histContent = t('The type of failure this asset has previously experienced, if any. Only failures matching the Failure Definition criteria are shown.');
	const gradeLabel = t('Grade');
	// prettier-ignore
	const gradeContent = t('Classification based on Failure Probability and Remaining Useful Life thresholds. Adjust thresholds in Grade Setup.');
	const materialLabel = t('Material');
	const materialContent = t('The pipe material recorded at the time the model was run.');
	const diameterLabel = t('Diameter');
	const diameterContent = t('The pipe diameter recorded at the time the model was run.');
	const installationYearLabel = t('Installation Year');
	const installationYearContent = t('The year the asset was installed, recorded at the time the model was run.');

	const history = useHistory();
	const dispatch = useDispatch();
	const { getSystemValueFormatted, getSystemUnit, system } = useUnits();

	const deteriorationModel = selectDeteriorationModel();
	const currentDeteriorationModel = deteriorationModel.deteriorationModel;
	const failureDefinition = selectFailureDefinition();
	// A model need not have a failure definition, and the failure definition is only
	// fetched when it does, so the model's own asset type comes first. Without it the
	// asset link built the literal path segment "undefined". The fallback is used only
	// when the stored failure definition is this model's: the slot is shared, so it can
	// still hold the previous model's.
	const ownFailureDefinition =
		failureDefinition.failureDefinition?.id === currentDeteriorationModel?.failureDefinitionId
			? failureDefinition.failureDefinition
			: null;
	const assetType =
		toAssetTypeId(currentDeteriorationModel?.systemType, currentDeteriorationModel?.assetType) ??
		toAssetTypeId(ownFailureDefinition?.systemType, ownFailureDefinition?.assetType);
	const modelId = currentDeteriorationModel?.id || '';
	const runId = currentDeteriorationModel?.latestRunResultsId || '';

	const storedPaginationState = selectModelPaginationState(modelId);
	const defaultRequest = getDefaultAssetsResultsRequest(modelId, runId);

	const [assetsResultsRequestState, setAssetsResultsRequestState] =
		useState<GetDeteriorationModelAssetsResultsPaginatedRequests>(() => {
			const storedRequest = storedPaginationState?.results.assets;
			if (storedRequest) {
				return {
					...storedRequest,
					modelId,
					runId,
				};
			}
			return defaultRequest;
		});
	// The request state is seeded once, on mount. Overlay the model's current ids so
	// a re-run (a new latestRunResultsId while this stays mounted) requests the new
	// run's results rather than the run that was current when this mounted.
	const assetsResultsRequest = useMemo<GetDeteriorationModelAssetsResultsPaginatedRequests>(
		() => ({ ...assetsResultsRequestState, modelId, runId }),
		[assetsResultsRequestState, modelId, runId],
	);

	const updateAssetsResultsRequest = (
		updater: (
			prev: GetDeteriorationModelAssetsResultsPaginatedRequests,
		) => GetDeteriorationModelAssetsResultsPaginatedRequests,
	) => {
		setAssetsResultsRequestState(prev => {
			const next = updater(prev);
			dispatch(
				setPaginationState({
					modelId,
					tab: 'assets',
					paginationRequest: next,
				}),
			);
			return next;
		});
	};
	const assetsResults = selectDeteriorationModelAssetsResults();

	useEffect(() => {
		// On returning from asset details this remounts with the same request the store
		// already answered, so skip the refetch rather than reload identical rows.
		if (hasStoredAssetsResults(assetsResults, assetsResultsRequest, currentDeteriorationModel?.latestRunResultsId)) {
			return;
		}
		dispatch(listDeteriorationModelAssetsResultsAction(assetsResultsRequest));
	}, [assetsResultsRequest, currentDeteriorationModel?.latestRunResultsId]);

	// When navigated here from the Groups view, pre-populate the search with the group name.
	useEffect(() => {
		if (!initialGroupFilter) return;
		updateAssetsResultsRequest(prev => ({
			...prev,
			searchValue: initialGroupFilter,
			page: 1,
		}));
	}, [initialGroupFilter]);

	const isGradeSetupEnabled = !!useIsFeatureEnabled('info360-venus-dm-grade-setup');
	const gradeAggregatedCounts = selectFailureGradeAggregatedCounts();
	const assetBreakDownData = selectAssetBreakDownChartStore();
	const failureGradeMatrixResults = selectFailureGradeMatrixResults();

	useEffect(() => {
		if (!isGradeSetupEnabled || modelId === '') return;

		// Refetch whenever the cached breakdown belongs to a different model or to an
		// earlier run, not merely when it is absent. Checking only for absence left the
		// previous model's figures on the grade cards and charts after navigating
		// between models, which reads as plausible data under the wrong model's name.
		const cachedMatchesCurrentRun =
			assetBreakDownData?.asset_breakdown !== null &&
			assetBreakDownData?.modelId === modelId &&
			assetBreakDownData?.runResultsId === runId;
		if (cachedMatchesCurrentRun) return;

		dispatch(getAssetBreakDownChartAction({ modelId, runResultsId: runId }));
		// isGradeSetupEnabled resolves from the async-loaded features slice. Without it
		// here, a flag that is still false on the render where modelId first arrives
		// would bail out and never retry, leaving the card lengths skeletonised.
	}, [modelId, runId, isGradeSetupEnabled]);

	useEffect(() => {
		if (!isGradeSetupEnabled || modelId === '') return;

		// Dispatch if matrix is empty OR if latestRunResultsId has changed (model re-run)
		const shouldFetchMatrix =
			!failureGradeMatrixResults?.matrix?.length ||
			failureGradeMatrixResults?.latestRunResultId !== currentDeteriorationModel?.latestRunResultsId;

		if (shouldFetchMatrix) {
			dispatch(
				getFailureGradeMatrixAction({
					modelId: modelId,
					latestRunResultId: currentDeteriorationModel?.latestRunResultsId || '',
				}),
			);
		}
	}, [
		modelId,
		currentDeteriorationModel?.latestRunResultsId,
		failureGradeMatrixResults?.matrix,
		failureGradeMatrixResults?.latestRunResultId,
	]);

	const handleRefresh = () => {
		dispatch(listDeteriorationModelAssetsResultsAction(assetsResultsRequest));
	};

	// Calculate percentages for each grade
	const calculatePercentage = (count: number): string => {
		if (gradeAggregatedCounts.total === 0) return '0%';
		return `${Math.round((count / gradeAggregatedCounts.total) * 100)}%`;
	};

	// Total pipe length for each grade, in km or miles per the tenant's display unit.
	const calculateLength = (typeName: string): string => {
		const materialData = assetBreakDownData?.asset_breakdown?.[typeName]?.material;
		if (!materialData) return formatTotalLength(0, system);

		// Every asset appears in exactly one material bucket, so summing the material
		// breakdown gives the grade's total length. Stored lengths are metres.
		const totalMetres = Object.values(materialData).reduce((sum, item) => sum + (item.length || 0), 0);
		return formatTotalLength(totalMetres, system);
	};

	const assetCardsConfig = [
		{
			topColor: '#8B5CF6',
			type: gradeAggregatedCounts.extreme,
			typeName: 'extreme',
			topText: t('Critical'),
		},
		{
			topColor: '#EC4899',
			type: gradeAggregatedCounts.high,
			typeName: 'high',
			topText: t('Concerning'),
		},
		{
			topColor: '#F59E0B',
			type: gradeAggregatedCounts.medium,
			typeName: 'medium',
			topText: t('Moderate'),
		},
		{
			topColor: '#14B8A6',
			type: gradeAggregatedCounts.low,
			typeName: 'low',
			topText: t('Stable'),
		},
		{
			topColor: '#3B82F6',
			type: gradeAggregatedCounts.negligible,
			typeName: 'negligible',
			topText: t('Optimal'),
		},
	];

	// The grade card whose breakdown chart is open. Seeded from, and written through to,
	// the store so it is still open after returning from asset details.
	const storedBreakdownGrade = selectSelectedBreakdownGrade(modelId);
	const [selectedAssetType, setSelectedAssetType] = useState<string>(storedBreakdownGrade);
	// Opening the breakdown when none is open animates it. Restoring it on mount, or
	// switching from one card to another, draws the new figures straight away. Left to
	// Highcharts, a switch animated only where it could match old points to new (the
	// same number of slices or years), so the three charts behaved differently.
	const [animateBreakdown, setAnimateBreakdown] = useState(!storedBreakdownGrade);
	const selectBreakdownGrade = (grade: string) => {
		setAnimateBreakdown(!selectedAssetType);
		setSelectedAssetType(grade);
		dispatch(setSelectedBreakdownGrade({ modelId, grade }));
	};

	// The DataGrid emits a spurious sort change on mount (its first-column default) that would
	// clobber the default "worst first" sort. Child effects run before parent effects, so this ref
	// is still false during the grid's mount emission and becomes true only for real user sorts.
	const sortReadyRef = useRef(false);
	useEffect(() => {
		sortReadyRef.current = true;
	}, []);

	// State for the Asset Curve Dialog.
	// Prev/Next navigation is scoped to the current page of results (assetsResults.rows); it
	// intentionally does not page across the full result set, so it stops at page boundaries.
	const [curveDialogAsset, setCurveDialogAsset] = useState<AssetCurveTarget | null>(null);
	const curveDialogIndex = curveDialogAsset
		? assetsResults.rows.findIndex(r => r.assetId === curveDialogAsset.assetId)
		: -1;
	const hasPrev = curveDialogIndex > 0;
	const hasNext = curveDialogIndex >= 0 && curveDialogIndex < assetsResults.rows.length - 1;
	const goToPrev = () =>
		hasPrev && setCurveDialogAsset(assetsResults.rows[curveDialogIndex - 1] as unknown as AssetCurveTarget);
	const goToNext = () =>
		hasNext && setCurveDialogAsset(assetsResults.rows[curveDialogIndex + 1] as unknown as AssetCurveTarget);

	return (
		<Stack direction="column" spacing={2}>
			{isGradeSetupEnabled && (
				<Stack direction="column" spacing={2}>
					<Box
						sx={{
							display: 'flex',
							gap: 1.5,
							'& .MuiCard-root': { boxShadow: 'none', border: '1px solid #e5e7eb' },
							'& .MuiCard-root *': { border: 'none' },
						}}>
						{assetCardsConfig.map(asset => {
							const isSelected = selectedAssetType === asset.typeName;
							return (
								<Box
									key={`assetCountCard-${asset.typeName}`}
									data-testid={`grade-card-${asset.typeName}`}
									sx={{
										flex: 1,
										cursor: 'pointer',
										position: 'relative',
										border: isSelected ? `2px solid ${asset.topColor}` : '2px solid transparent',
										borderRadius: 1,
										transition: 'border-color 0.2s ease-in-out',
										'&:hover .see-breakdown': { opacity: 1 },
									}}
									onClick={() => selectBreakdownGrade(isSelected ? '' : asset.typeName)}>
									<StylovyzeCard
										bottomTextRight={<Typography variant="body2">{calculatePercentage(asset.type)}</Typography>}
										bottomTextLeft={
											assetBreakDownData?.asset_breakdown ? (
												<Typography variant="body2">{calculateLength(asset.typeName)}</Typography>
											) : (
												<Skeleton variant="text" width={40} height={16} />
											)
										}
										topColor={asset.topColor}
										countText={asset.type}
										titlePosition="above"
										topSubText="Assets"
										topText={asset.topText}
									/>
									<Box
										className="see-breakdown"
										sx={{
											opacity: isSelected ? 1 : 0,
											transition: 'opacity 0.15s ease',
											position: 'absolute',
											bottom: 0,
											left: 0,
											right: 0,
											textAlign: 'center',
											fontSize: '0.75rem',
											fontWeight: 400,
											color: '#0696D7',
											py: '8px',
											backgroundColor: '#ffffff',
											borderTop: '1px solid #f3f4f6',
											borderRadius: '0 0 4px 4px',
											'&:hover': { textDecoration: 'underline' },
										}}>
										{isSelected ? t('Hide breakdown ↑') : t('See breakdown ↓')}
									</Box>
								</Box>
							);
						})}
					</Box>
					{selectedAssetType && (
						<AssetBreakDownChart
							type={selectedAssetType}
							topText={assetCardsConfig.find(asset => asset.typeName === selectedAssetType)?.topText || ''}
							onHide={() => selectBreakdownGrade('')}
							animate={animateBreakdown}
						/>
					)}
				</Stack>
			)}
			<DataGrid
				weave
				// loading
				loading={assetsResults.isLoading}
				skeletonRowCount={assetsResults.pagination.slice}
				// columns and headers
				columns={(() => {
					const baseColumns: GridColDef[] = [
						{
							field: 'assetId',
							headerName: t('Asset ID'),
							renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Asset ID')}</Text>,
							flex: 1,
							type: 'string',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
							renderCell: params => {
								if (!assetType) {
									return (
										<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={params.row.assetId}>
											{params.row.assetId}
										</span>
									);
								}
								return (
									<Link
										underline="hover"
										style={{ overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
										title={params.row.assetId}
										onClick={() => {
											dispatch(setAssetBackLabel(t('Back to Deterioration Model')));
											history.push(
												addRouteNamespace(
													`asset-details/${encodeRouteParam(assetType)}/${encodeRouteParam(params.row.assetId)}`,
												),
											);
										}}>
										{params.row.assetId}
									</Link>
								);
							},
						},
						{
							field: 'assetGroupName',
							headerName: t('Group'),
							renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Group')}</Text>,
							flex: 1,
							type: 'string',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
						},
						{
							field: 'material',
							headerName: materialLabel,
							renderHeader: () => renderHeaderWithTooltip(materialLabel, materialLabel, materialContent),
							flex: 1,
							type: 'string',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
							renderCell: params => {
								if (params.value == null) return '-';
								return params.value;
							},
						},
						{
							field: 'diameter',
							headerName: diameterLabel,
							renderHeader: () => renderHeaderWithTooltip(diameterLabel, diameterLabel, diameterContent),
							flex: 1,
							type: 'number',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
							renderCell: params => {
								if (params.value == null) return '-';
								return getSystemValueFormatted(`${params.value}mm`, '-', 1) ?? '-';
							},
						},
						{
							field: 'installationYear',
							headerName: installationYearLabel,
							renderHeader: () =>
								renderHeaderWithTooltip(installationYearLabel, installationYearLabel, installationYearContent),
							flex: 1,
							type: 'number',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
							renderCell: params => {
								if (params.value == null) return '-';
								return params.value;
							},
						},
						{
							field: 'failureProbability',
							headerName: fpLabel,
							renderHeader: () => renderHeaderWithTooltip(fpLabel, fpHeading, fpContent),
							flex: 1,
							type: 'number',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
							renderCell: params => {
								if (params.value == null) return '-';
								// Highlight in red once the asset has crossed the 80% failure threshold.
								const num = Number(params.value);
								const isCritical = !Number.isNaN(num) && num >= 80;
								return (
									<Text style={{ color: isCritical ? '#ef4444' : 'inherit', fontWeight: isCritical ? 600 : 'normal' }}>
										{params.value}
									</Text>
								);
							},
						},
						{
							field: 'rul',
							headerName: rulLabel,
							renderHeader: () => renderHeaderWithTooltip(rulLabel, rulHeading, rulContent),
							flex: 1,
							type: 'number',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
							renderCell: params => {
								if (params.value == null) return '-';
								// Negative RUL means the asset has already passed the failure threshold.
								const num = Number(params.value);
								const isPast = !Number.isNaN(num) && num < 0;
								return (
									<Text style={{ color: isPast ? '#ef4444' : 'inherit', fontWeight: isPast ? 600 : 'normal' }}>
										{params.value}
									</Text>
								);
							},
						},
						{
							field: 'age',
							headerName: t('Age (Years)'),
							renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Age (Years)')}</Text>,
							flex: 1,
							type: 'number',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
						},
						{
							field: 'failureYear',
							headerName: t('Expected Failure Year'),
							renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Expected Failure Year')}</Text>,
							flex: 1,
							type: 'number',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
						},
						{
							field: 'historicalFailure',
							headerName: histLabel,
							renderHeader: () => renderHeaderWithTooltip(histLabel, histLabel, histContent),
							flex: 1,
							type: 'string',
							filterable: true,
							align: 'left',
							headerAlign: 'left',
						},
					];

					if (isGradeSetupEnabled) {
						baseColumns.push({
							field: 'grade',
							headerName: gradeLabel,
							renderHeader: () => renderHeaderWithTooltip(gradeLabel, gradeLabel, gradeContent),
							flex: 1,
							type: 'singleSelect',
							valueOptions: GRADE_VALUE_OPTIONS,
							// Exclude 'not' — backend STRING fields don't support "is not"
							filterOperators: getGridSingleSelectOperators().filter(op => op.value !== 'not'),
							filterable: true,
							align: 'left',
							headerAlign: 'left',
							renderCell: params => <GradeChip grade={params.row.grade} />,
						});
					}

					baseColumns.push({
						field: 'seeCurve',
						headerName: '',
						renderHeader: () => <></>,
						width: 90,
						sortable: false,
						filterable: false,
						align: 'left',
						headerAlign: 'left',
						renderCell: params => (
							<Link
								underline="hover"
								sx={{ cursor: 'pointer', fontSize: '0.875rem', color: '#0696D7' }}
								onClick={() => setCurveDialogAsset(params.row as AssetCurveTarget)}>
								{t('See curve')}
							</Link>
						),
					});

					return baseColumns;
				})()}
				disableColumnMenu
				disableColumnPinning
				disableColumnReorder
				// footer
				footerHelper={t('{{from}}-{{to}} of {{total}}', {
					from: (assetsResults.pagination.page - 1) * assetsResults.pagination.slice + 1,
					to: Math.min(assetsResults.pagination.page * assetsResults.pagination.slice, assetsResults.pagination.total),
					total: assetsResults.pagination.total,
				})}
				// toolbar actions
				disableRowSelectionOnClick
				// Custom toolbar using slots
				slots={{
					toolbar: CustomDataGridHeader,
				}}
				slotProps={{
					toolbar: {
						search: {
							value: assetsResultsRequest.searchValue || '',
							onChange: (searchValue: string) => {
								updateAssetsResultsRequest(prev => ({
									...prev,
									searchValue,
								}));
							},
							onCancelSearch: () => {
								updateAssetsResultsRequest(prev => ({
									...prev,
									searchValue: null,
								}));
							},
							placeholder: t('Search by Asset Group'),
						},
						filterToolbar: true,
						refreshButton: {
							onRefresh: handleRefresh,
						},
						exportProps: {
							source: 'pipe.deterioration.export.create',
							total: assetsResults.pagination.total,
							requestParams: {
								...(assetsResultParamsMapping(
									{
										...assetsResultsRequest,
										page: assetsResults.pagination.page,
										slice: assetsResults.pagination.slice,
									},
									true,
								) as Record<string, string>),
								export_type: 'pipe-deterioration-export',
								results_type: 'assets',
								model_id: currentDeteriorationModel?.id || '',
								run_results_id: currentDeteriorationModel?.latestRunResultsId || '',
								tenant_id: getTenantId() || '',
								with_grade: isGradeSetupEnabled ? 'true' : 'false',
							},
						},
						weave: true,
						loading: assetsResults.isLoading,
					},
					filterPanel: {
						logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
					},
				}}
				// filter
				filterMode="server"
				filterModel={assetsResultsRequest.filterModel as GridFilterModel}
				onFilterModelChange={model => {
					const hasItems = !!model.items && model.items.length > 0;

					const filter: NonNullable<GetDeteriorationModelAssetsResultsPaginatedRequests['filter']> = {};

					if (hasItems) {
						model.items.forEach(item => {
							if (!item.field) return;
							const noValueOperators = ['is empty', 'is not empty', 'isEmpty', 'isNotEmpty'];
							if (!noValueOperators.includes(item.operator) && (item.value == null || item.value === '')) return;
							const entry = {
								operator: item.operator,
								value: item.value ?? null,
							};
							if (item.field === 'assetId') filter.assetId = entry;
							else if (item.field === 'assetGroupName') filter.assetGroupName = entry;
							else if (item.field === 'failureProbability') filter.failureProbability = entry;
							else if (item.field === 'age') filter.age = entry;
							else if (item.field === 'rul') filter.rul = entry;
							else if (item.field === 'failureYear') filter.failureYear = entry;
							else if (item.field === 'historicalFailure') filter.historicalFailure = entry;
							else if (item.field === 'grade') filter.grade = entry;
							else if (item.field === 'material') filter.material = entry;
							else if (item.field === 'diameter')
								// Typed in the display unit, compared server-side in mm.
								filter.diameter = diameterFilterToMetric(
									entry,
									system,
									getSystemUnit('mm'),
									(measurement, decimals, metricUnit) =>
										convertToMetricNoIntlFormat(measurement, decimals, metricUnit).value,
								);
							else if (item.field === 'installationYear') filter.installationYear = entry;
						});
					}

					updateAssetsResultsRequest(prev => ({
						...prev,
						filter: hasItems && Object.keys(filter).length > 0 ? filter : null,
						filterModel: model,
						page: 1,
					}));
				}}
				// sorting
				sortingMode="server"
				sortingOrder={['desc', 'asc']}
				disableMultipleColumnsSorting
				// The grid otherwise defaults its sort to the first column (assetId). Seed the initial
				// sort from the request (default failureProbability desc, or restored stored sort) instead.
				initialState={{
					sorting: {
						sortModel: [
							{
								field: assetsResultsRequest.sortKey,
								sort: assetsResultsRequest.sortDescending ? 'desc' : 'asc',
							},
						],
					},
				}}
				onSortModelChange={sm => {
					// Ignore the grid's spurious mount-time emission so the default sort holds on load.
					if (!sortReadyRef.current) return;
					// MUI can emit an empty sort model (e.g. transiently on mount). Never go unsorted —
					// fall back to the default "worst first" sort so the table loads failureProbability desc.
					const active = sm.length ? sm[0] : { field: 'failureProbability', sort: 'desc' };
					const sortKey = active?.field || 'failureProbability';
					const sortDescending = active?.sort !== 'asc';
					updateAssetsResultsRequest(prev => ({
						...prev,
						sortKey,
						sortDescending,
					}));
				}}
				// rows
				rows={assetsResults.rows}
				rowCount={assetsResults.pagination.total}
				// pagination
				pagination
				pageSizeOptions={[5, 10, 15, 25, 50, 100]}
				paginationMode="server"
				paginationModel={{
					page: assetsResults.pagination.page - 1,
					pageSize: assetsResults.pagination.slice,
				}}
				onPaginationModelChange={model => {
					updateAssetsResultsRequest(prev => ({
						...prev,
						page: model.page + 1,
						slice: model.pageSize,
					}));
				}}
			/>
			<AssetCurveDialog
				asset={curveDialogAsset}
				open={!!curveDialogAsset}
				onClose={() => setCurveDialogAsset(null)}
				onPrev={goToPrev}
				onNext={goToNext}
				hasPrev={hasPrev}
				hasNext={hasNext}
				modelId={modelId}
				runId={runId}
			/>
		</Stack>
	);
};
